/**
 * DmPersonaContext — the currently impersonated DM persona (NOT the admin gate).
 *
 * The outer admin login is held in AuthContext. After admin auth, the operator
 * picks a user from the DM Users page and signs in as that user via a modal;
 * the resulting access token (from auth-api-serverless email-code flow) is what
 * powers all `/api/v0/dm/*` calls.
 *
 * One persona at a time — switching personas calls `logout()` then `login()`.
 * Persisted in localStorage["fancian-admin-dm-persona"].
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { decodeJwtPayload, isJwtExpired } from '../utils/jwt';

const STORAGE_KEY = 'fancian-admin-dm-persona';

const DmPersonaContext = createContext(null);

function readPersisted() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.accessToken) return null;
    if (isJwtExpired(parsed.accessToken)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function DmPersonaProvider({ children }) {
  const [state, setState] = useState({
    accessToken: null,
    userId: null,
    username: '',
    deviceId: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const persisted = readPersisted();
    if (persisted) {
      setState({
        accessToken: persisted.accessToken,
        userId: persisted.userId,
        username: persisted.username || '',
        deviceId: persisted.deviceId || '',
      });
    } else if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (state.accessToken && state.userId) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [state, loading]);

  const login = useCallback((accessToken, fallback = {}) => {
    const claims = decodeJwtPayload(accessToken) || {};
    const userIdRaw = claims.user_id ?? fallback.userId;
    const userId =
      typeof userIdRaw === 'number' ? userIdRaw : Number(userIdRaw) || null;
    const username = claims.username || fallback.username || '';
    const deviceId = claims.device_id || fallback.deviceId || '';
    setState({ accessToken, userId, username, deviceId });
  }, []);

  const logout = useCallback(() => {
    setState({ accessToken: null, userId: null, username: '', deviceId: '' });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      loading,
      login,
      logout,
      isAuthenticated: Boolean(state.accessToken && state.userId),
    }),
    [state, loading, login, logout]
  );

  return <DmPersonaContext.Provider value={value}>{children}</DmPersonaContext.Provider>;
}

export function useDmPersona() {
  const ctx = useContext(DmPersonaContext);
  if (!ctx) throw new Error('useDmPersona must be used within DmPersonaProvider');
  return ctx;
}
